import { useState } from 'react';
import { X } from 'lucide-react';
import type { Team, User } from '../../types';
import { useApp } from '../../context/AppContext';
import Avatar from '../common/Avatar';

interface TeamFormProps {
  team?: Team;
  onClose: () => void;
}

export default function TeamForm({ team, onClose }: TeamFormProps) {
  const { state, apiCreateTeam, apiUpdateTeam } = useApp();
  const [name, setName] = useState(team?.name || '');
  const [description, setDescription] = useState(team?.description || '');
  const [color, setColor] = useState(team?.color || '#3570E6');
  const [leadId, setLeadId] = useState(team?.leadId || '');
  const [memberIds, setMemberIds] = useState<string[]>(team?.memberIds || []);
  const [addMemberId, setAddMemberId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const availableMembers = state.users.filter((u) => !memberIds.includes(u.id));

  const handleAddMember = (userId: string) => {
    if (userId && !memberIds.includes(userId)) {
      setMemberIds([...memberIds, userId]);
    }
    setAddMemberId('');
  };

  const handleRemoveMember = (userId: string) => {
    setMemberIds(memberIds.filter((id) => id !== userId));
    if (leadId === userId) setLeadId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || submitting) return;

    setSubmitting(true);

    if (team) {
      await apiUpdateTeam({
        ...team,
        name: name.trim(),
        description: description.trim(),
        color,
        leadId,
        memberIds,
      });
    } else {
      await apiCreateTeam({
        name: name.trim(),
        description: description.trim(),
        color,
        leadId,
        memberIds,
      });
    }

    setSubmitting(false);
    onClose();
  };

  const getMemberUser = (id: string): User | undefined =>
    state.users.find((u) => u.id === id);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">
            {team ? 'Editar Equipe' : 'Nova Equipe'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cor</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Líder</label>
            <select
              value={leadId}
              onChange={(e) => setLeadId(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
            >
              <option value="">Selecionar líder...</option>
              {state.users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Membros</label>
            <div className="space-y-2 mb-2">
              {memberIds.map((id) => {
                const member = getMemberUser(id);
                if (!member) return null;
                return (
                  <div key={id} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-xl">
                    <Avatar name={member.name} color={member.avatarColor} size="sm" />
                    <span className="text-sm text-slate-700 flex-1">{member.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(id)}
                      className="p-0.5 rounded text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
            <select
              value={addMemberId}
              onChange={(e) => handleAddMember(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
            >
              <option value="">Adicionar membro...</option>
              {availableMembers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!name.trim() || submitting}
              className="flex-1 px-4 py-2.5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-40 transition-colors"
            >
              {submitting ? 'Salvando...' : team ? 'Salvar' : 'Criar Equipe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
